/**
 * CSVの読み取り(共通)。
 * 現行スプレッドシートからのエクスポートは文字コードが UTF-8 / Shift_JIS の
 * どちらも来うるため、両対応にする(yojitsu-system の銀行CSV取込と同方式)。
 */

export type CsvTable = { headers: string[]; rows: string[][] };

/** UTF-8 で読めなければ Shift_JIS として読み直す */
export async function readCsvFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  try {
    const text = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
    return text.replace(/^﻿/, '');
  } catch {
    return new TextDecoder('shift_jis').decode(buffer);
  }
}

/** RFC4180 準拠の簡易パーサ(引用符・改行入りセル・CRLFに対応) */
export function parseCsv(text: string): CsvTable {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += c;
      }
      continue;
    }
    if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(cell);
      cell = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      cell = '';
      if (row.some((v) => v.trim() !== '')) rows.push(row);
      row = [];
    } else {
      cell += c;
    }
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    if (row.some((v) => v.trim() !== '')) rows.push(row);
  }

  const headers = (rows[0] ?? []).map((h) => h.trim());
  return { headers, rows: rows.slice(1) };
}

/** ファイル内容のハッシュ(重複取込の防止に使う。Phase 2 の日次取込と共通) */
export async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
